/* The user's own limits. Everything the rule engine compares against lives here
   so the numbers can be changed without going near the logic. */

/* The size ceilings that used to sit here are stored records now — see
   `CapBandRecord` in types.ts and `SEED_CAP_BANDS` in data/seed.ts. They left
   because they are a naming system, not a threshold: the nine below were one
   person's, and the ceiling on "Gambling position" is not something this file
   can ship. What stays is every number that is genuinely a threshold. */
export const LIMITS = {
  documentAbove:     1,  // above this % of the book, a position needs its thesis written
  riskPerPosition: 1.5,  // max % of the whole book lost if one abort fires
  themeLimit:       60,  // max % of the book on any one named theme
  cashFloor:         5,  // min % in cash
  cashFloorHot:     10,  // when the cycle reading is hot
  trimTriggerMult: 1.5,  // over 1.5x the top of its band -> trim
  planBeforeEarningsOver: 15,  // positions above this % need a written plan pre-earnings
  pauseDrawdown:    20,  // % below peak -> stop opening new positions for 14 days
  deRiskDrawdown:   30,  // % below peak -> cut equity exposure to half
  staleResidualDays: 30, // leftover after a rotation, unresolved this long -> close or re-underwrite
} as const;


/** Windows that are policy rather than thresholds. */
export const WINDOWS = {
  earningsPlanDays: 14,   // "earnings within 14 days" for planBeforeEarnings
  pauseDays: 14,          // how long the drawdown pause lasts
  thesisChurnCount: 3,    // rewrites since last earnings that count as churn
} as const;

/* The capital gains rate used to live here as a constant 19%, which is one
   country's answer wearing the clothes of arithmetic. It is a setting now —
   see `rules/tax.ts`, where picking a country fills it in and anywhere the app
   does not know asks rather than guesses. */

/* ---------------- runtime overrides ---------------- */

/* §4 keeps these numbers in this file so they can be changed without going near
   the logic, and §7.5 also puts them behind a settings screen. Both work: the
   values above are the defaults, and anything the user edits in settings is
   stored as an override and merged on top at read time. Nothing in the engine
   reads the constant directly — it takes the merged result. */

export interface Limits {
  documentAbove: number;
  riskPerPosition: number;
  themeLimit: number;
  cashFloor: number;
  cashFloorHot: number;
  trimTriggerMult: number;
  planBeforeEarningsOver: number;
  pauseDrawdown: number;
  deRiskDrawdown: number;
  staleResidualDays: number;
}

/* `positionCaps` was here and is a stored record now. The key is kept on the
   type as legacy: a book written before the migration carries edited ceilings
   under it, they are folded into the band records once at version 12, and
   nothing writes it again. Removing it outright would make that fold
   untypeable. */
export type LimitOverrides =
  Partial<Limits> & { positionCaps?: Record<string, number> };

export function mergeLimits(overrides?: LimitOverrides): Limits {
  const { positionCaps: _legacy, ...rest } = overrides ?? {};
  return { ...LIMITS, ...rest };
}

/** The default, used wherever no user override is in play. */
export const DEFAULT_LIMITS: Limits = mergeLimits();

/** Labels for the settings screen, in the order they read best. */
export const LIMIT_FIELDS: Array<{ key: keyof Limits; label: string; unit: string }> = [
  { key: 'documentAbove', label: 'Size that needs a written thesis', unit: '%' },
  { key: 'riskPerPosition', label: 'Most the book can lose to one abort', unit: '%' },
  { key: 'themeLimit', label: 'Most the book can put on one theme', unit: '%' },
  { key: 'cashFloor', label: 'Cash floor', unit: '%' },
  { key: 'cashFloorHot', label: 'Cash floor when the cycle is hot', unit: '%' },
  { key: 'trimTriggerMult', label: 'Multiple of the cap that triggers a trim', unit: 'x' },
  { key: 'planBeforeEarningsOver', label: 'Size that needs a written plan before earnings', unit: '%' },
  { key: 'pauseDrawdown', label: 'Drawdown that pauses new positions', unit: '%' },
  { key: 'deRiskDrawdown', label: 'Drawdown that halves equity exposure', unit: '%' },
  { key: 'staleResidualDays', label: 'Days a rotation leftover can sit unresolved', unit: 'days' },
];
